import { daysBetween, todayIso } from '../../shared/data/common';

export const airlines = [
  { code: 'AI', name: 'Air India', color: '#e63946' },
  { code: '6E', name: 'IndiGo', color: '#1d3fbb' },
  { code: 'UK', name: 'Vistara', color: '#5f2a6b' },
  { code: 'SG', name: 'SpiceJet', color: '#f4a261' },
  { code: 'EK', name: 'Emirates', color: '#c8102e' },
  { code: 'SQ', name: 'Singapore Airlines', color: '#f2a900' },
];

export const airlineLogo = (code) => `https://pics.avs.io/120/120/${code}.png`;

// [airline, dep, arr, duration, stops, price, layover]
const templates = [
  ['6E', '06:10', '08:20', '2h 10m', 0, 4899],
  ['AI', '07:45', '10:05', '2h 20m', 0, 5620],
  ['UK', '09:30', '11:45', '2h 15m', 0, 6240],
  ['SG', '12:15', '16:40', '4h 25m', 1, 4150, 'Hyderabad'],
  ['6E', '14:50', '17:05', '2h 15m', 0, 5310],
  ['EK', '17:20', '23:55', '6h 35m', 1, 12890, 'Dubai'],
  ['AI', '19:40', '21:55', '2h 15m', 0, 7020],
  ['SQ', '22:10', '06:30', '8h 20m', 1, 15400, 'Singapore'],
];

export const popularRoutes = [
  ['Delhi', 'Mumbai', 4899], ['Mumbai', 'Goa', 2999], ['Bengaluru', 'Delhi', 5499], ['Delhi', 'Dubai', 11999],
  ['Mumbai', 'Singapore', 14999], ['Chennai', 'Bangkok', 9899], ['Delhi', 'London', 38999], ['Kolkata', 'Bali', 17499],
];

const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const toTime = (m) => { m = ((m % 1440) + 1440) % 1440; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; };
const durMin = (d) => { const [h, m] = d.match(/\d+/g).map(Number); return h * 60 + m; };
const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;

export function generateFlights(from = 'Delhi', to = 'Mumbai') {
  return templates.map(([code, dep, arr, duration, stops, price, layover], i) => {
    const flightNo = `${code}-${100 + i * 37}`;
    const airline = airlines.find((a) => a.code === code);
    const total = durMin(duration);
    let segments;
    if (stops) {
      const leg1 = Math.round(total * 0.4);
      const wait = 75;
      const leg2 = total - leg1 - wait;
      const via = [layover, 'Hyderabad', 'Bengaluru', 'Dubai'].find((c) => c !== from && c !== to);
      segments = [
        { from, to: via, dep, arr: toTime(toMin(dep) + leg1), duration: fmtDur(leg1), flightNo },
        { from: via, to, dep: toTime(toMin(dep) + leg1 + wait), arr, duration: fmtDur(leg2), flightNo: `${code}-${600 + i * 11}`, layover: fmtDur(wait) },
      ];
    } else {
      segments = [{ from, to, dep, arr, duration, flightNo }];
    }
    return {
      id: flightNo, airline, flightNo, from, to, dep, arr, duration, stops, price, segments,
      refundable: i % 3 !== 0,
      overnight: toMin(arr) < toMin(dep),
      aircraft: code === 'EK' || code === 'SQ' ? 'Boeing 777-300ER' : i % 2 ? 'Airbus A320neo' : 'Airbus A321',
    };
  });
}

export const getFlight = (id, from, to) => generateFlights(from, to).find((f) => f.id === id);

// Fare families shown on the flight details page.
export const fareFamilies = (flight) => [
  { key: 'saver', name: 'Saver', price: flight.price, cabinBag: '7 kg', checkin: '15 kg', meal: 'Paid meals',
    seat: 'Paid seats', change: '₹2,999 + fare difference', cancel: flight.refundable ? '₹3,500 per traveller' : 'Non-refundable', cancelFee: flight.refundable ? 3500 : null },
  { key: 'flexi', name: 'Flexi', price: Math.round(flight.price * 1.12), cabinBag: '7 kg', checkin: '20 kg', meal: 'Complimentary meal',
    seat: 'Free standard seat', change: 'Free (fare difference applies)', cancel: '₹2,000 per traveller', cancelFee: 2000, tag: 'Popular' },
  { key: 'superflexi', name: 'Super Flexi', price: Math.round(flight.price * 1.25), cabinBag: '7 kg', checkin: '25 kg', meal: 'Complimentary meal',
    seat: 'Free seat of choice', change: 'Free', cancel: 'Free cancellation', cancelFee: 0 },
];

export const CHILD_FACTOR = 0.75;
export const INFANT_FARE = 1500;

// Refund estimate used on the booking details page.
export function flightRefund(booking) {
  const { fare } = booking;
  const pax = booking.summary.pax.adults + booking.summary.pax.children;
  const fee = booking.summary.cancelFee;
  if (daysBetween(todayIso(), booking.travelDate) < 0) return { amount: 0, note: 'Flight has already departed.' };
  if (fee == null) return { amount: Math.round(fare.taxes * 0.6), note: 'Non-refundable fare — only statutory taxes are refunded.' };
  const amount = Math.max(0, fare.total - fee * pax - 299);
  return { amount, note: fee ? `Airline cancellation fee ${'₹' + fee.toLocaleString('en-IN')} × ${pax} + ₹299 Trawtel fee` : 'Free cancellation fare. ₹299 Trawtel fee applies.' };
}
